import config from '@/config';
import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import {
  useGetApplicationsQuery,
  useGetTenantCredentialsQuery,
  useUpdateApplicationMutation,
} from '@features/apps';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { PageContainer, PageHeading } from '@/components/layout';

/**
 * Workspace settings page
 */
export const SettingsPage = () => {
  const { appId, tenantId, app, isLoading } = useApp();
  const navigate = useNavigate();

  // Form state
  const [workspaceName, setWorkspaceName] = useState('');
  const [description, setDescription] = useState('');

  // UI state
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showApiKeysDialog, setShowApiKeysDialog] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [updateApplication, { isLoading: isUpdating }] = useUpdateApplicationMutation();
  const { refetch: refetchApplications } = useGetApplicationsQuery();

  // Fetch tenant credentials when API Keys dialog is opened
  const { data: credentials, isLoading: isLoadingCredentials } = useGetTenantCredentialsQuery(
    appId!,
    {
      skip: !showApiKeysDialog || !appId,
    }
  );

  // Initialize form when app data loads
  useEffect(() => {
    if (app) {
      setWorkspaceName(app.name || '');
      setDescription(app.description || '');
    }
  }, [app]);

  const handleSaveChanges = async () => {
    if (!appId) return;

    try {
      await updateApplication({
        tenantId,
        appId,
        data: {
          name: workspaceName,
          description,
        },
      }).unwrap();

      setSnackbar({
        open: true,
        message: 'Workspace settings updated successfully',
        severity: 'success',
      });
    } catch (_err) {
      setSnackbar({
        open: true,
        message: 'Failed to update workspace settings',
        severity: 'error',
      });
    }
  };

  const handleArchiveWorkspace = async () => {
    if (!appId) return;

    try {
      await updateApplication({
        tenantId,
        appId,
        data: {
          archived: true,
        },
      }).unwrap();

      // Refetch applications to update the list
      await refetchApplications();

      // Show success message
      setSnackbar({
        open: true,
        message: 'Workspace archived successfully',
        severity: 'success',
      });

      // Navigate to home after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (_err) {
      setSnackbar({
        open: true,
        message: 'Failed to archive workspace',
        severity: 'error',
      });
      setShowArchiveDialog(false);
    }
  };

  const handleManageApiKeys = () => {
    setShowApiKeysDialog(true);
    // Query will automatically start when dialog opens (skip becomes false)
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <PageContainer>
      <PageHeading title="Settings" subtitle="Manage workspace settings and configuration" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            General
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton height={56} />
              <Skeleton height={56} />
              <Skeleton height={100} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Workspace Name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Unique Key"
                value={app.uniqueKey || tenantId}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                helperText="This unique identifier is used in API calls and data mappings"
              />
              <TextField
                label="API Endpoint"
                value={`${config.EXT_DATA_API_URL}@self`}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                helperText="Base API endpoint URL for this workspace"
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
                placeholder="No description."
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveChanges}
                  loading={isUpdating}
                  disabled={!workspaceName.trim()}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage API keys for programmatic access to this workspace.
          </Typography>
          <Button variant="outlined" onClick={handleManageApiKeys}>
            Manage API Keys
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error">
            Danger Zone
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="body1">Archive Workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                This will hide the workspace from your list. Data will be preserved.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setShowArchiveDialog(true)}
              disabled={isUpdating}
            >
              Archive
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={showArchiveDialog}
        onClose={(_event, reason) => {
          // Only close when Cancel button is clicked, not on backdrop click or escape
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setShowArchiveDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Archive Workspace</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive the <strong>{app.name}</strong> workspace?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            This will hide the workspace from your list. All data will be preserved and can be
            restored later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowArchiveDialog(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleArchiveWorkspace}
            color="error"
            variant="contained"
            loading={isUpdating}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Keys Dialog */}
      <Dialog
        open={showApiKeysDialog}
        onClose={(_event, reason) => {
          // Only close when Close button is clicked, not on backdrop click or escape
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setShowApiKeysDialog(false);
          setShowSecret(false);
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>API Keys</DialogTitle>
        <DialogContent>
          {isLoadingCredentials ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="API Key"
                value={credentials?.tykApiKey || 'In Process'}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Secret"
                value={credentials?.tykSecretKey || 'In Process'}
                type={showSecret ? 'text' : 'password'}
                fullWidth
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecret(!showSecret)}
                          edge="end"
                          size="small"
                        >
                          {showSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ mb: 2 }}
              />
              {(!credentials?.tykApiKey || !credentials?.tykSecretKey) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Your API Credentials are being provisioned. You will be notified when they are
                    available (less than 24 hours).
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Use the "Help" button to ask any questions.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowApiKeysDialog(false);
              setShowSecret(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};
